import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { XIcon, CheckCircleIcon, TrophyIcon, ArrowRightIcon, SparklesIcon, AwardIcon, StarIcon, TrendingUpIcon, LockIcon } from 'lucide-react';
interface CourseCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseTitle: string;
  onContinue: () => void;
  hasNextCourse: boolean;
  // Training 2 specific props
  isTraining2?: boolean;
  pointsEarned?: number;
}
export function CourseCompletionModal({
  isOpen,
  onClose,
  courseTitle,
  onContinue,
  hasNextCourse,
  isTraining2 = false,
  pointsEarned = 100
}: CourseCompletionModalProps) {
  // Training 2 specific content
  if (isTraining2) {
    return <AnimatePresence data-id="element-738">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-739" />

            {/* Modal */}
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4" data-id="element-740">
              <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} transition={{
            type: 'spring',
            duration: 0.5
          }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden relative" data-id="element-741">
                {/* Close button */}
                <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10" data-id="element-742">
                  <XIcon className="w-5 h-5 text-gray-500" data-id="element-743" />
                </button>

                {/* Confetti background effect */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-744">
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.2
              }} className="absolute inset-0" style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(33, 163, 102, 0.15) 0%, transparent 70%)'
              }} data-id="element-745" />
                  {/* Animated sparkles */}
                  {[...Array(15)].map((_, i) => <motion.div key={i} initial={{
                opacity: 0,
                scale: 0,
                rotate: 0
              }} animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                rotate: [0, 180, 360],
                x: [0, Math.random() * 200 - 100],
                y: [0, Math.random() * 200 - 100]
              }} transition={{
                duration: 2,
                delay: i * 0.1,
                repeat: Infinity,
                repeatDelay: 1
              }} className="absolute" style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`
              }} data-id="element-746">
                      <SparklesIcon className="w-4 h-4 text-yellow-400" data-id="element-747" />
                    </motion.div>)}
                </div>

                {/* Content */}
                <div className="relative p-8 text-center" data-id="element-748">
                  {/* Success Icon */}
                  <motion.div initial={{
                scale: 0,
                rotate: -180
              }} animate={{
                scale: 1,
                rotate: 0
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15,
                delay: 0.2
              }} className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center shadow-xl bg-gradient-to-br from-emerald-400 via-green-500 to-teal-600" data-id="element-749">
                    <TrophyIcon className="w-14 h-14 text-white" data-id="element-750" />
                  </motion.div>

                  {/* Title */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3
              }} data-id="element-751">
                    <div className="flex items-center justify-center space-x-2 mb-3" data-id="element-752">
                      <AwardIcon className="w-6 h-6 text-yellow-500" data-id="element-753" />
                      <h2 className="text-3xl font-bold text-[#132C53]" data-id="element-754">
                        Felicitări! 🎉
                      </h2>
                      <AwardIcon className="w-6 h-6 text-yellow-500" data-id="element-755" />
                    </div>
                    <p className="text-lg text-gray-600 mb-2" data-id="element-756">
                      Ai finalizat cu succes
                    </p>
                    <p className="text-2xl font-bold text-[#357BEB] mb-6" data-id="element-757">
                      Training 2 – Proceduri și Tranzacții Închiriere
                    </p>
                  </motion.div>

                  {/* Career Level Advancement */}
                  <motion.div initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.4,
                type: 'spring'
              }} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 mb-6 border-2 border-purple-200" data-id="element-758">
                    <div className="flex items-center justify-center space-x-3 mb-3" data-id="element-759">
                      <TrendingUpIcon className="w-8 h-8 text-purple-600" data-id="element-760" />
                      <div data-id="element-761">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-762">
                          Ai avansat la nivelul de carieră
                        </p>
                        <p className="text-3xl font-black text-purple-600" data-id="element-763">
                          Rental Closer
                        </p>
                      </div>
                      <TrendingUpIcon className="w-8 h-8 text-purple-600" data-id="element-764" />
                    </div>
                    <p className="text-sm text-purple-700 font-semibold" data-id="element-765">
                      🎯 Noul tău nivel de competență în închirieri!
                    </p>
                  </motion.div>

                  {/* Points Earned */}
                  <motion.div initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.5,
                type: 'spring'
              }} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-amber-200" data-id="element-766">
                    <div className="flex items-center justify-center space-x-3 mb-2" data-id="element-767">
                      <StarIcon className="w-8 h-8 text-amber-500 fill-amber-500" data-id="element-768" />
                      <div data-id="element-769">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-770">
                          Ai câștigat
                        </p>
                        <p className="text-4xl font-black text-amber-600" data-id="element-771">
                          +{pointsEarned}
                        </p>
                      </div>
                      <StarIcon className="w-8 h-8 text-amber-500 fill-amber-500" data-id="element-772" />
                    </div>
                    <p className="text-sm font-semibold text-amber-700" data-id="element-773">
                      puncte pentru finalizarea Training 2! 🎉
                    </p>
                  </motion.div>

                  {/* Next Training Requirements */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.6
              }} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 mb-6 border-2 border-blue-200" data-id="element-774">
                    <div className="flex items-start space-x-3" data-id="element-775">
                      <LockIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-776" />
                      <div className="text-left" data-id="element-777">
                        <h4 className="font-bold text-[#132C53] mb-2" data-id="element-778">
                          Următorul Training
                        </h4>
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-779">
                          Următorul training va fi disponibil după finalizarea a{' '}
                          <span className="font-bold text-blue-600" data-id="element-780">
                            minim 1 tranzacție de închiriere
                          </span>
                          .
                        </p>
                        <p className="text-xs text-blue-600 font-semibold mt-2" data-id="element-781">
                          💡 Aplică cunoștințele dobândite în practică!
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Achievements Summary */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.7
              }} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 mb-6 border-2 border-emerald-200" data-id="element-782">
                    <h4 className="font-bold text-[#132C53] mb-3 flex items-center justify-center" data-id="element-783">
                      <CheckCircleIcon className="w-5 h-5 mr-2 text-emerald-600" data-id="element-784" />
                      Ce ai realizat
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700 text-left" data-id="element-785">
                      <li className="flex items-start space-x-2" data-id="element-786">
                        <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-787" />
                        <span data-id="element-788">
                          Ai învățat procedurile complete pentru tranzacții de
                          închiriere
                        </span>
                      </li>
                      <li className="flex items-start space-x-2" data-id="element-789">
                        <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-790" />
                        <span data-id="element-791">
                          Ai avansat la nivelul de carieră "Rental Closer"
                        </span>
                      </li>
                      <li className="flex items-start space-x-2" data-id="element-792">
                        <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-793" />
                        <span data-id="element-794">
                          Ești pregătit să finalizezi prima ta tranzacție de
                          închiriere
                        </span>
                      </li>
                    </ul>
                  </motion.div>

                  {/* Action Buttons */}
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.8
              }} className="flex flex-col gap-3" data-id="element-795">
                    <Link to="/dashboard" onClick={onClose} className="w-full px-6 py-4 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 flex items-center justify-center space-x-2" data-id="element-796">
                      <span data-id="element-797">Mergi la Dashboard</span>
                      <ArrowRightIcon className="w-5 h-5" data-id="element-798" />
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>;
  }
  // Default completion modal for other trainings
  return <AnimatePresence data-id="element-799">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-800">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.9,
        opacity: 0
      }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-801">
            {/* Confetti Background Effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-802">
              <div className="absolute top-0 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-bounce" data-id="element-803"></div>
              <div className="absolute top-10 right-1/4 w-3 h-3 bg-blue-400 rounded-full animate-bounce delay-75" data-id="element-804"></div>
              <div className="absolute top-5 left-1/2 w-2 h-2 bg-green-400 rounded-full animate-bounce delay-150" data-id="element-805"></div>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-8 relative" data-id="element-806">
              <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors" data-id="element-807">
                <XIcon className="w-6 h-6" data-id="element-808" />
              </button>

              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-809">
                <TrophyIcon className="w-12 h-12 text-white" data-id="element-810" />
              </motion.div>

              <motion.h3 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-3xl font-bold text-center mb-2" data-id="element-811">
                Felicitări! 🎉
              </motion.h3>
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className="text-white text-opacity-90 text-center" data-id="element-812">
                Ai finalizat cu succes training-ul
              </motion.p>
            </div>

            {/* Content */}
            <div className="p-8" data-id="element-813">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 mb-6 border-2 border-emerald-200" data-id="element-814">
                <div className="flex items-start space-x-3" data-id="element-815">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center" data-id="element-816">
                    <CheckCircleIcon className="w-6 h-6 text-white" data-id="element-817" />
                  </div>
                  <div className="flex-1" data-id="element-818">
                    <h4 className="font-bold text-emerald-900 mb-1" data-id="element-819">
                      {courseTitle}
                    </h4>
                    <p className="text-sm text-emerald-700" data-id="element-820">
                      Toate lecțiile au fost completate cu succes
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="space-y-3 mb-6" data-id="element-821">
                <div className="flex items-center space-x-3 text-gray-700" data-id="element-822">
                  <SparklesIcon className="w-5 h-5 text-yellow-500" data-id="element-823" />
                  <span className="text-sm" data-id="element-824">
                    Progresul tău a fost salvat automat
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-gray-700" data-id="element-825">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" data-id="element-826" />
                  <span className="text-sm" data-id="element-827">
                    Certificatul de finalizare este disponibil în profil
                  </span>
                </div>
                {hasNextCourse && <div className="flex items-center space-x-3 text-gray-700" data-id="element-828">
                    <ArrowRightIcon className="w-5 h-5 text-blue-500" data-id="element-829" />
                    <span className="text-sm" data-id="element-830">
                      Următorul training este acum disponibil
                    </span>
                  </div>}
              </motion.div>

              {/* CTA Buttons */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} className="space-y-3" data-id="element-831">
                {hasNextCourse ? <>
                    <button onClick={onContinue} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB] text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center space-x-2" data-id="element-832">
                      <span data-id="element-833">Continuă la următorul training</span>
                      <ArrowRightIcon className="w-5 h-5" data-id="element-834" />
                    </button>
                    <Link to="/cursuri" onClick={onClose} className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors text-center block" data-id="element-835">
                      Vezi toate training-urile
                    </Link>
                  </> : <>
                    <Link to="/cursuri" onClick={onClose} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB] text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center space-x-2" data-id="element-836">
                      <span data-id="element-837">Vezi toate training-urile</span>
                      <ArrowRightIcon className="w-5 h-5" data-id="element-838" />
                    </Link>
                    <Link to="/dashboard" onClick={onClose} className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors text-center block" data-id="element-839">
                      Înapoi la Dashboard
                    </Link>
                  </>}
              </motion.div>

              {/* Info note */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.8
          }} className="mt-6 pt-6 border-t border-gray-200" data-id="element-840">
                <p className="text-xs text-gray-500 text-center" data-id="element-841">
                  💡 Poți reveni oricând la lecțiile finalizate pentru a le
                  revizui
                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}